import React, { memo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

/**
 * Pill — a small badge/chip component.
 *
 * Can be rendered as a passive label or an interactive toggle pill.
 *
 * Props:
 * @param {string}      label      – Text to display inside the pill.
 * @param {() => void} [onClick]   – Click handler. If provided, renders a <button>; otherwise a <span>.
 * @param {boolean}    [isToggle]  – When true, applies toggle styling (border, pointer, hover/focus).
 */
const Pill = memo(({ label, onClick, isToggle }) => {
  // Base pill styling: shape & typography
  const baseClasses = 'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium';

  // Variant: normal vs toggle
  const variantClasses = isToggle
    ? 'bg-white text-blue-600 border border-blue-400 cursor-pointer hover:bg-blue-50 focus:outline-none'
    : 'bg-gray-200 text-gray-700';

  // Choose element type
  const Tag = onClick ? 'button' : 'span';

  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={clsx(baseClasses, variantClasses)}
    >
      {label}
    </Tag>
  );
});

Pill.propTypes = {
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  isToggle: PropTypes.bool,
};

export default Pill;
